import { lazy, Suspense } from "react";
import Box from "@mui/material/Box";
import { Navigate, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import AppLayout from "../../layouts/app/AppLayout";
import MinimalLayout from "../../layouts/minimal/MinimalLayout";
import PublicLayout from "../../layouts/public/PublicLayout";
import LoginPage from "../../features/auth/pages/LoginPage";
import CompleteSignupPage from "../../features/auth/pages/CompleteSignupPage";
import SelectAccountPage from "../../features/auth/pages/SelectAccountPage";
import RootRedirect from "./RootRedirect";
import ProtectedRoute from "./guards/ProtectedRoute";
import EmailVerifiedRoute from "./guards/EmailVerifiedRoute";
import RouteFallback from "./fallback/RouteFallback";
import { clearLogoutSessionCache } from "../auth/authSessionState";

const ChannelPageList = lazy(() => import(/* webpackPrefetch: true */ "../../features/channels/pages/ChannelsOverviewPage"));
const ChannelPageOptions = lazy(() => import("../../features/channels/pages/ChannelAddPage"));
const CreateIntegrationAlcancePage = lazy(() => import("../../features/channels/pages/providers/sms-alcance/SmsAlcanceIntegrationPage"));
const CreateIntegrationSesPage = lazy(() => import("../../features/channels/pages/providers/email-ses/EmailSesIntegrationPage"));
const CreateIntegrationEmbeddedPage = lazy(() => import("../../features/channels/pages/providers/whatsapp-api/WhatsAppApiEmbeddedSignupPage"));
const ChatbotBuilderPage = lazy(() => import("../../features/chatbots/pages/ChatbotBuilderPage"));

const TemplatesPageList = lazy(() => import(/* webpackPrefetch: true */ "../../features/templates/pages/TemplatesOverviewPage"));
const TemplatePageAdd = lazy(() => import("../../features/templates/options"));
const TemplatePageChannelAdd = lazy(() => import("../../features/templates/create"));

const IntegrationsPageAdd = lazy(() => import(/* webpackPrefetch: true */ "../../features/integrations/options"));
const AddIntegration = lazy(() => import("../../features/integrations/AddIntegration"));

const PasswordRecoveryPage = lazy(() => import("../../features/auth/pages/ForgotPasswordPage"));
const CreatePasswordPage = lazy(() => import("../../features/auth/pages/CreatePasswordPage"));
const DashboardPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/dashboard"));
const AccountSettingsPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/account"));

const CampaignCreatePage = lazy(() => import("../../features/campaigns/create/CampaignCreatePage"));
const CampaignEditPage = lazy(() => import("../../features/campaigns/edit/CampaignEditPage"));
const CampaignPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/campaigns/overview"));
const CampaignViewPage = lazy(() => import("../../features/campaigns/view/CampaignViewPage"));

const Sheets = lazy(() => import(/* webpackPrefetch: true */ "../../features/data-sources/overview"));
const AddSheetsOptions = lazy(() => import("../../features/data-sources/options"));
const PageAddGoogleSheets = lazy(() => import("../../features/data-sources/types/GoogleSheets/create/addGoogleSheets"));
const PageAddExcel = lazy(() => import("../../features/data-sources/types/Excel/create/addExcel"));
const PageAddApiRest = lazy(() => import("../../features/data-sources/types/ApiRest/create/addApiRest"));
const AddSheetsIntegrationInvoice = lazy(() => import("../../features/data-sources/types/Erps/create/addSheetsIntegrationInvoice"));

const SignUp = lazy(() => import("../../features/auth/pages/SignupPage"));
const VerifyEmailPage = lazy(() => import("../../features/auth/pages/VerifyEmailPage"));

const NotFound = lazy(() => import("../../shared/components/patterns/NotFound"));
const SessionExpired = lazy(() => import("../../shared/components/patterns/SessionExpired"));
const DevPlayground = lazy(() => import("../dev/DevPlayground"));

const SupportAccessPage = lazy(() => import("../../features/auth/pages/SupportAccessPage"));
const ResetRequiredPasswordPage = lazy(() => import("../../features/auth/pages/ResetPasswordPage"));
const WhatsappTestePage = lazy(() => import("../../features/channels/pages/WhatsAppTestPage"));

const appRoutePrefixes = [
  "/painel",
  "/canais",
  "/modelos",
  "/listagens",
  "/campanhas",
  "/integrações",
  "/configuracoes",
];

function getRouteShellKey(pathname) {
  if (
    appRoutePrefixes.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    )
  ) {
    return "app-shell";
  }

  if (
    pathname === "/verificar-email" ||
    pathname === "/finalizar-cadastro" ||
    pathname === "/selecionar-conta"
  ) {
    return "auth-minimal-shell";
  }

  return "public-shell";
}

function LogoutSessionExpiredRoute() {
  const navigate = useNavigate();

  const handleLoginRedirect = () => {
    clearLogoutSessionCache();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  return <SessionExpired onAction={handleLoginRedirect} />;
}

export default function AppRoutes() {
  const location = useLocation();
  const routeShellKey = getRouteShellKey(location.pathname);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Box
        key={routeShellKey}
        sx={{
          animation: "routeShellIn 120ms ease-out both",
          minWidth: 0,
          width: "100%",
          "@keyframes routeShellIn": {
            from: {
              opacity: 0.92,
            },
            to: {
              opacity: 1,
            },
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <Routes>
          {process.env.NODE_ENV !== "production" && (
            <Route path="/_dev" element={<DevPlayground />} />
          )}

          <Route element={<PublicLayout />}>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/esqueci-senha" element={<PasswordRecoveryPage />} />
            <Route path="/criar-senha" element={<CreatePasswordPage />} />
            <Route path="/cadastro" element={<SignUp />} />
            <Route path="/support-login" element={<SupportAccessPage />} />
            <Route path="/reset-senha" element={<ResetRequiredPasswordPage />} />
            <Route path="/whatsapp_teste" element={<WhatsappTestePage />} />
            <Route path="/logout" element={<LogoutSessionExpiredRoute />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route element={<MinimalLayout />}>
              <Route path="/verificar-email" element={<VerifyEmailPage />} />
              <Route path="/selecionar-conta" element={<SelectAccountPage />} />
            </Route>

            <Route element={<EmailVerifiedRoute />}>
              <Route element={<MinimalLayout />}>
                <Route path="/finalizar-cadastro" element={<CompleteSignupPage />} />
              </Route>

              <Route element={<AppLayout />}>
                <Route path="/painel" element={<DashboardPage />} />
                <Route path="/configuracoes/*" element={<AccountSettingsPage />} />

                <Route path="/canais" element={<ChannelPageList />} />
                <Route path="/canais/adicionar" element={<ChannelPageOptions />} />
                <Route path="/canais/adicionar/whatsapp-api" element={<CreateIntegrationEmbeddedPage />} />
                <Route path="/canais/adicionar/sms" element={<CreateIntegrationAlcancePage />} />
                <Route path="/canais/adicionar/email" element={<CreateIntegrationSesPage />} />
                <Route path="/canais/chatbot" element={<ChatbotBuilderPage />} />
                <Route path="/canais/configurar" element={<Navigate to="/canais/chatbot" replace />} />

                <Route path="/modelos" element={<TemplatesPageList />} />
                <Route path="/modelos/canal" element={<TemplatePageAdd />} />
                <Route path="/modelos/canal/whatsapp-api" element={<TemplatePageChannelAdd channel="whatsapp_api" />} />
                <Route path="/modelos/canal/email" element={<TemplatePageChannelAdd channel="email" />} />
                <Route path="/modelos/canal/sms" element={<TemplatePageChannelAdd channel="sms" />} />

                <Route path="/listagens" element={<Sheets />} />
                <Route path="/listagens/adicionar" element={<AddSheetsOptions />} />
                <Route path="/listagens/adicionar/google" element={<PageAddGoogleSheets />} />
                <Route path="/listagens/adicionar/api" element={<PageAddApiRest />} />
                <Route path="/listagens/adicionar/excel" element={<PageAddExcel />} />
                <Route path="/listagens/adicionar/:integration" element={<AddSheetsIntegrationInvoice />} />
                <Route path="/listagens/adicionar/:integration/faturas em aberto" element={<AddSheetsIntegrationInvoice />} />

                <Route path="/campanhas" element={<CampaignPage />} />
                <Route path="/campanhas/adicionar" element={<CampaignCreatePage />} />
                <Route path="/campanhas/:id/editar" element={<CampaignEditPage />} />
                <Route path="/campanhas/:id/analisar" element={<CampaignViewPage />} />

                <Route path="/integrações" element={<IntegrationsPageAdd />} />
                <Route path="/integrações/adicionar" element={<AddIntegration />} />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
    </Suspense>
  );
}
